import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, IsActiveMatchOptions, NavigationEnd, Router, isActive } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  combineLatest,
  fromEvent,
  Observable,
  of,
  ReplaySubject,
  Subscription,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
  catchError,
  NEVER,
  Subject,
} from 'rxjs';

import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { AppState } from '@fcom/core/interfaces/app-state.interface';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { CountryState } from '@fcom/core/interfaces/country-state.interface';
import { StorageService } from '@fcom/core/services/storage/storage.service';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { WindowRef } from '@fcom/core/providers/window';
import { loginIsOpen, loginStatus } from '@fcom/core/selectors/login.selector';
import { LoginStatus, LoginStep } from '@fcom/core-api/login/interfaces/login.interface';
import { isPresent } from '@fcom/core/utils/utils';
import { stopPropagation } from '@fcom/core/utils/event';
import { finShare } from '@fcom/rx/operators/finShare';
import { snapshot } from '@fcom/rx/utils/snapshot';
import { CmsTemplate, CmsViewType } from '@fcom/core-api/interfaces/cms-interface';
import { ButtonMode, ButtonSize, ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';
import * as LoginActions from '@fcom/core/actions/login.actions';
import { DapiService } from '@fcom/dapi/api/dapi.service';

import { fetchOrderPreviews } from '../../../dapi/api/fn/order/fetch-order-previews';
import { CmsPageService } from '../../services/cms/page.service';
import { MediaQueryService } from '../../services/media-query/media-query.service';
import { NavigationMenuService } from '../../services/navigation-menu/navigation-menu.service';
import { FinnairThemeService } from '../../services/theme/finnair-theme.service';
import { FinnairTheme } from '../../interfaces/theme.interface';
import { BreadcrumbItem, MenuItem, MenuJson } from '../../interfaces/menu.interface';
import { ConsentStatus } from '../../interfaces/consents.interface';
import { ElementActions, ElementTypes, GaContext } from '../../interfaces/gtm.interface';
import { onOpenGtmEvent } from '../../interfaces/join-finnair-plus.interface';
import { ConsentService } from '../../login/services/consent.service';
import { JoinService } from '../../login/services/join.service';
import { LoginService } from '../../login/services/login.service';
import { LoginWidgetComponent } from '../../login/components/login-widget/login-widget.component';
import {
  getMobileAppVerificationSuccessUrl,
  MOBILE_APP_CHANNEL,
} from '../../login/interfaces/join-finnair-plus.interface';
import { isNavigationEvent } from '../../utils/route-utils';
import { LanguageSelectorContext } from '../language-selector/language-selector.component';

interface ExtendedMenuItem extends MenuItem {
  trackingId: string;
}

const UPCOMING_TRIPS_STORAGE_KEY = 'amountOfUpcomingTrips';
const UPCOMING_TRIPS_MAX_AMOUNT = 5;

const FLAT_HEADER_PATHS = ['', '/manage', '/manage/check-in'];
const SCROLL_ELEVATION_THRESHOLD = 48;

@Component({
  selector: 'fin-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnDestroy {
  private navigationMenuService = inject(NavigationMenuService);
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);
  private loginService = inject(LoginService);
  private store$ = inject<Store<AppState>>(Store);
  private mediaQueryService = inject(MediaQueryService);
  private gtmService = inject(GtmService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private pageService = inject(CmsPageService);
  private consentService = inject(ConsentService);
  joinService = inject(JoinService);
  private dapiService = inject(DapiService);
  private storageService = inject(StorageService);
  private readonly windowRef = inject(WindowRef);
  private themeService = inject(FinnairThemeService);
  private platformId = inject(PLATFORM_ID);

  isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  private readonly isMobile = this.mediaQueryService.isBreakpoint('mobile');
  private readonly isTablet = this.mediaQueryService.isBreakpoint('tablet');

  mobileOrTablet = computed(() => {
    return this.isMobile() || this.isTablet();
  });

  private readonly isMobile$ = toObservable(this.isMobile);
  private readonly mobileOrTablet$ = toObservable(this.mobileOrTablet);

  private readonly headerMatchOptions: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };

  private readonly currentLang = toSignal(this.languageService.lang, {
    initialValue: this.languageService.langValue,
  });

  private readonly flatHeaderRouteActive = computed(() => {
    const lang = this.currentLang();
    return FLAT_HEADER_PATHS.map((path) => {
      return isActive(`/${lang}${path}`, this.router, this.headerMatchOptions);
    });
  });

  isHomeManageOrCheckInRoute = computed(() => {
    return this.flatHeaderRouteActive().some((active) => active());
  });

  expandedCategory = toSignal(this.navigationMenuService.expandedNaviCategory, { initialValue: null });

  private scrollY = signal(0);

  elevationProgress = computed(() => {
    if (!this.isNewBrand()) {
      return 0;
    }
    if (!this.isHomeManageOrCheckInRoute()) {
      return 1;
    }
    return Math.min(this.scrollY() / SCROLL_ELEVATION_THRESHOLD, 1);
  });

  @ViewChild(LoginWidgetComponent, { static: false }) loginWidget: LoginWidgetComponent;

  readonly ButtonSize = ButtonSize;
  readonly CustomIcon = CustomIcon;
  readonly IconLibrary = IconLibrary;
  readonly IconPosition = IconPosition;
  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly ElementTypes = ElementTypes;
  readonly GaContext = GaContext;
  readonly homeLink$: Observable<string>;
  readonly currentLanguage$: Observable<string>;
  readonly LoginStatus = LoginStatus;
  readonly enableWelcomeBackMessage: boolean = false;
  readonly isVisible$: Observable<boolean>;
  readonly LANGUAGE_SELECTOR_CONTEXT = LanguageSelectorContext.TOP_NAVI;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  isMenuOpen$: Observable<boolean>;
  countries$: Observable<CountryState>;
  loginStatus$: Observable<LoginStatus>;
  oneworldLink$: Observable<BreadcrumbItem>;

  menuItems$: Observable<MenuJson>;
  topMenuItems$: Observable<ExtendedMenuItem[]>;
  expandedOrActiveNaviCategory$: Observable<MenuItem>;
  activeNaviCategory$: Observable<MenuItem>;
  isOpen = false;
  pageHasDisruptions$: Observable<boolean>;
  enableLoginDialog = false;
  enableJoinDialog = false;
  loginDialogOpen$: Observable<boolean>;
  authOverlayOpen$: Observable<boolean>;
  cookiePolicy$: Observable<boolean>;
  isLandingPage$: Observable<boolean>;
  welcomeBackMessageOpen$: Observable<boolean>;
  amountOfUpcomingTrips$: Observable<string>;
  accountVerificationToken$ = new ReplaySubject<string>(1);
  verificationChannel$ = new ReplaySubject<string>(1);
  verificationSuccessUrl$ = new ReplaySubject<string | undefined>(1);
  subscriptions = new Subscription();
  loginWidgetOpen$ = new Subject<boolean>();

  constructor() {
    this.isVisible$ = this.navigationMenuService.isHeaderVisible$;
    this.isMenuOpen$ = this.navigationMenuService.isNavigationMenuOpen$;
    this.homeLink$ = this.languageService.lang.pipe(map((lang) => `/${lang}`));
    this.enableWelcomeBackMessage = this.configService.cfg.enableWelcomeBackMessage;
    this.enableLoginDialog = this.configService.cfg.enableLoginDialog;
    this.enableJoinDialog = this.configService.cfg.enableJoinDialog;
  }

  ngOnInit(): void {
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());
    this.oneworldLink$ = this.navigationMenuService.oneworldLink$;
    this.menuItems$ = this.navigationMenuService.menuItems$;
    const matchUrlLocalRegex = /^\/\w+-\w+\//;
    this.topMenuItems$ = this.menuItems$.pipe(
      map((menuItems) =>
        menuItems.top.map((menuItem) => ({
          ...menuItem,
          trackingId: menuItem.link.match(matchUrlLocalRegex) ? menuItem.link.replace(matchUrlLocalRegex, '') : 'book',
        }))
      )
    );
    this.expandedOrActiveNaviCategory$ = this.navigationMenuService.expandedOrActiveNaviCategory$;
    this.activeNaviCategory$ = this.navigationMenuService.activeNaviCategory;
    const pagePath$ = this.router.events.pipe(
      filter<NavigationEnd>(isNavigationEvent),
      map((navigation) => navigation.url),
      startWith(this.router.url),
      distinctUntilChanged()
    );

    const amountOfUpcomingTrips$: Observable<number> = this.loginStatus$.pipe(
      distinctUntilChanged(),
      switchMap((status) => {
        if (status === LoginStatus.INITIAL || status === LoginStatus.PENDING) {
          return of(0);
        }
        if (status !== LoginStatus.LOGGED_IN) {
          this.storageService.SESSION.remove(UPCOMING_TRIPS_STORAGE_KEY);
          return of(0);
        }
        return this.dapiService
          .invoke$(fetchOrderPreviews, this.configService.cfg.orderUrl, {
            locale: this.languageService.localeValue,
          })
          .pipe(
            take(1),
            map((response) => response?.orderPreviews?.length || 0),
            tap((orderPreviews) => {
              this.storageService.SESSION.set(UPCOMING_TRIPS_STORAGE_KEY, orderPreviews);
            }),
            catchError(() => NEVER),
            startWith(this.storageService.SESSION.get(UPCOMING_TRIPS_STORAGE_KEY) || 0)
          );
      }),
      finShare()
    );

    this.amountOfUpcomingTrips$ = amountOfUpcomingTrips$.pipe(
      map((amount) => {
        if (amount > 0) {
          return amount <= UPCOMING_TRIPS_MAX_AMOUNT ? amount.toString() : `${UPCOMING_TRIPS_MAX_AMOUNT}+`;
        }
        return '';
      }),
      finShare()
    );

    this.pageHasDisruptions$ = pagePath$.pipe(
      switchMap((path) => this.pageService.getPageDataIfExists(path)),
      filter<CmsTemplate>(Boolean),
      map((template) => template.main),
      map((contents) =>
        contents.find(
          ({ viewTypeName }) => isPresent(viewTypeName) && viewTypeName.endsWith(CmsViewType.FRONT_PAGE_DISRUPTIONS)
        )
      ),
      map((disruptions) => disruptions?.items.length > 0),
      startWith(false)
    );

    this.cookiePolicy$ = this.consentService.cookieConsents$.pipe(
      map((cg) => cg?.consents?.every((c) => c.consentStatus !== ConsentStatus.WITHDRAWN)),
      startWith(false),
      finShare()
    );

    this.loginDialogOpen$ = combineLatest([this.loginStatus$, this.store$.pipe(loginIsOpen())]).pipe(
      map(([status, isOpen]) => status !== LoginStatus.INITIAL && status !== LoginStatus.PENDING && isOpen)
    );
    this.authOverlayOpen$ = combineLatest([
      this.loginDialogOpen$,
      this.joinService.joinDialogOpen$,
      this.isMobile$,
      this.loginWidgetOpen$.pipe(startWith(false)),
    ]).pipe(
      map(
        ([loginDialogOpen, joinDialogOpen, mobile, loginWidgetOpen]) =>
          !mobile && (loginDialogOpen || joinDialogOpen || loginWidgetOpen)
      ),
      distinctUntilChanged(),
      finShare()
    );

    this.isLandingPage$ = pagePath$.pipe(map((routeValue) => new RegExp('^/[^/]+$').test(routeValue)));

    this.welcomeBackMessageOpen$ = combineLatest([
      this.loginDialogOpen$,
      this.joinService.joinDialogOpen$,
      this.pageHasDisruptions$,
      this.cookiePolicy$,
      of(this.enableWelcomeBackMessage),
      this.loginStatus$,
      this.isLandingPage$,
      this.mobileOrTablet$.pipe(startWith(false)),
    ]).pipe(
      map(
        ([
          loginDialogOpen,
          joinDialogOpen,
          pageHasDisruptions,
          cookiePolicy,
          enableWelcomeBackMessage,
          loginStatus,
          isLandingPage,
          mobileOrTablet,
        ]) =>
          !loginDialogOpen &&
          !joinDialogOpen &&
          !pageHasDisruptions &&
          cookiePolicy &&
          enableWelcomeBackMessage &&
          loginStatus === LoginStatus.NOT_LOGGED_IN &&
          isLandingPage &&
          !mobileOrTablet
      )
    );

    const token = this.route.snapshot.queryParamMap.get('verify-account') ?? '';
    this.accountVerificationToken$.next(token);
    const channelParam = this.configService.cfg.enableMobileAppVerificationRedirect
      ? (this.route.snapshot.queryParamMap.get('channel') ?? '').toLowerCase()
      : '';
    const channel = channelParam === MOBILE_APP_CHANNEL ? MOBILE_APP_CHANNEL : '';
    const verificationSuccessUrl =
      channel === MOBILE_APP_CHANNEL
        ? getMobileAppVerificationSuccessUrl(
            this.route.snapshot.queryParamMap.get('app-id'),
            this.configService.cfg.mobileAppVerificationSuccessUrls
          )
        : undefined;
    this.verificationChannel$.next(channel);
    this.verificationSuccessUrl$.next(verificationSuccessUrl);
    if (token) {
      this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
    }

    if (isPlatformBrowser(this.platformId)) {
      this.scrollY.set(this.windowRef.nativeWindow.scrollY);
      this.subscriptions.add(
        fromEvent(this.windowRef.nativeWindow, 'scroll', { passive: true }).subscribe(() => {
          this.scrollY.set(this.windowRef.nativeWindow.scrollY);
        })
      );
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  openLoginWidget(event: MouseEvent): void {
    stopPropagation(event);
    this.loginWidget.toggle();
  }

  closeNaviMenu(): void {
    this.navigationMenuService.closeNaviMenu();
    this.isOpen = false;
  }

  setCategoryAsExpanded(event: Event, item: MenuItem): void {
    this.navigationMenuService.setCategoryAsExpanded(event, item);
  }

  clickOutside(): void {
    this.isOpen = false;
    this.navigationMenuService.closeNaviMenu();
  }

  handleLoginButtonClick(event: MouseEvent, loginStatus: LoginStatus): void {
    if (loginStatus === LoginStatus.LOGGED_IN) {
      this.openLoginWidget(event);
    }

    if (loginStatus === LoginStatus.NOT_LOGGED_IN) {
      this.openLoginDialog();
    }
  }

  toggleNavigation(event: Event): void {
    stopPropagation(event);

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.navigationMenuService.setExpandedCategory(snapshot(this.expandedOrActiveNaviCategory$));
    } else {
      this.navigationMenuService.closeNaviMenu();
    }

    this.gtmService.trackElement(
      'desktop-main-navigation-hamburger-navi',
      GaContext.DESKTOP_MAIN_NAVIGATION,
      ElementTypes.ACCORDION,
      undefined,
      this.isOpen ? ElementActions.OPEN : ElementActions.CLOSE
    );
  }

  headerGtmClickEvent(gtmId: string, gtmContext: GaContext): void {
    this.gtmService.trackElement(gtmId, gtmContext, ElementTypes.BUTTON, undefined, ElementActions.CLICK);
  }

  joinFinnairPlus(): void {
    if (this.enableJoinDialog) {
      this.loginWidget.close();
      this.joinService.joinDialogOpen$.next(true);
      this.headerGtmClickEvent(`join-form-open-${onOpenGtmEvent}`, GaContext.FINNAIR_PLUS);
    } else {
      this.loginService.redirectToJoin();
    }
  }

  openLoginDialog(): void {
    if (this.enableLoginDialog) {
      this.loginWidget.close();
      this.joinService.joinDialogOpen$.next(false);
      this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
    } else {
      this.loginService.redirectToLogin();
    }
  }

  closeLoginDialog(): void {
    this.store$.dispatch(LoginActions.clearLoginPhase());
  }

  scrollToTop(): void {
    this.windowRef.nativeWindow.scrollTo({ top: 0, behavior: 'smooth' });
  }

  accountVerificationFinished(): void {
    this.accountVerificationToken$.next(null);
  }
}
